import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { RegularPayments } from '../_models/payments';
import { RegularPaymentsService } from '../services/regular-payments.service';
import { MatDialog } from '@angular/material';
import { DeleteDialogComponent } from '@app/driver-tariffs/delete-dialog/delete-dialog.component';
import { Subscription } from 'rxjs';
import { TariffService } from '../services/tariff.service';
import { ConvertTimeExpressionService } from '@app/shared/services/convert-time-expression.service';

@Component({
  selector: 'app-regular-payments',
  templateUrl: './regular-payments.component.html',
  styleUrls: ['./regular-payments.component.scss']
})
export class RegularPaymentsComponent implements OnInit, OnDestroy {
  regularPaymentsForm: FormGroup;
  paymentsArray: FormArray;
  private clearFormSubs: Subscription;
  isDirty = false;
  paymentsType = ['all', 'sending'];
  paymentsAmountType = ['total', 'unit'];
  isSavedPayment = false;
  regularPayments: RegularPayments[] = [];
  paimentOfTariff: RegularPayments[] = [];
  selectedPayment: RegularPayments;
  @Input() selectedIndex = 0;
  @Input() operation;

  paymentsPeriods = [
    {
      value: 'DAILY',
      id: 'daily'
    },
    {
      value: 'WEEKLY',
      id: 'weekly'
    }
  ];
  daysOfWeek = [
    {
      name: 'MONDAY',
      id: 1
    },
    {
      name: 'TUESDAY',
      id: 2
    },
    {
      name: 'WEDNESDAY',
      id: 3
    },
    {
      name: 'THURSDAY',
      id: 4
    },
    {
      name: 'FRIDAY',
      id: 5
    },
    {
      name: 'SATURDAY',
      id: 6
    },
    {
      name: 'SUNDAY',
      id: 0
    }
  ];

  constructor(
    private fb: FormBuilder,
    private regularPaymentsService: RegularPaymentsService,
    private dialog: MatDialog,
    private tariffService: TariffService,
    private expressionsService: ConvertTimeExpressionService
  ) {}

  get paymentsFormArray(): FormArray {
    return this.regularPaymentsForm.get('payments') as FormArray;
  }

  ngOnInit() {
    this.clearFormSubs = this.tariffService.clearFormEvent.subscribe(isClear => {
      if (isClear) {
        this.clearFormArray();
        this.buildPaymentFormArray(this.paimentOfTariff);
        this.selectedIndex = 0;
        this.selectedPayment = this.paimentOfTariff[0];
      }
    });
    this.paymentsArray = this.fb.array([this.buildPaymentsFomsControls()]);
    this.initPaymentsForm();
    this.regularPaymentsForm.valueChanges.pipe(debounceTime(100)).subscribe(formsValues => {
      this.isDirty = this.regularPaymentsForm.dirty;
      this.regularPaymentsService.getIsDirtyForm(this.isDirty);
      if (!formsValues.payments[formsValues.payments.length - 1].id) {
        this.isSavedPayment = false;
      } else {
        this.isSavedPayment = true;
      }
      this.buildRegularPaymentsArray(formsValues.payments);
      this.regularPaymentsService.validationRagularPaymentsState.next(this.isDirty ? this.paymentsArray.valid : true);
      this.regularPaymentsService.getRegularPaymentArray(this.regularPayments);
    });
  }

  @Input('regularPaymentsOfTariff')
  set regularPaymentsOfTariff(regularPaymentsOfTariff: RegularPayments[]) {
    if (!regularPaymentsOfTariff) {
      return;
    }
    this.isDirty = false;
    this.regularPaymentsService.getIsDirtyForm(this.isDirty);
    this.paimentOfTariff = regularPaymentsOfTariff;
    this.selectedPayment = regularPaymentsOfTariff && regularPaymentsOfTariff[this.selectedIndex];
    this.clearFormArray();
    this.buildPaymentFormArray(regularPaymentsOfTariff);
    if (regularPaymentsOfTariff.length - 1 < this.selectedIndex) {
      this.selectedIndex = 0;
    }
  }

  initDaysFrom(day, id, el?) {
    return this.fb.group({
      id: id,
      day: day,
      start_time: [
        el && el.start_time ? el.start_time : '00:00',
        Validators.compose([
          Validators.maxLength(5),
          Validators.required,
          Validators.pattern('^(((([0-1][0-9])|(2[0-3])):?[0-5][0-9])|(23:?00))')
        ])
      ],
      end_time: [
        el && el.end_time ? el.end_time : '00:00',
        Validators.compose([
          Validators.maxLength(5),
          Validators.required,
          Validators.pattern('^(((([0-1][0-9])|(2[0-3])):?[0-5][0-9])|(23:?00))')
        ])
      ],
      start_day: [el && el.start_day ? el.start_day : 0, Validators.required],
      end_day: [el && el.end_day ? el.end_day : 0, Validators.required]
    });
  }

  getExpression(obj) {
    if (obj.period == 'daily') {
      return this.expressionsService.getFormAnObjectToSend(
        this.timeValueSource({
          days_of_week: obj.days || [],
          bonuses_mode: 'SINGLE_DAYS',
          start_time: obj.start_time,
          end_time: obj.end_time
        })
      ).expression;
    } else {
      return this.expressionsService.getFormAnObjectToSend(
        this.timeValueSource({
          bonuses_mode: 'CONTINUOUS_DAYS',
          start_time: obj.start_time,
          end_time: obj.end_time,
          start_day: obj.start_day,
          end_day: obj.end_day
        })
      ).expression;
    }
  }

  private buildRegularPaymentsArray(data: RegularPayments[]) {
    if (data) {
      data.forEach((el, i) => {
        this.regularPayments[i] = {
          name: el.name,
          loc_name: el.loc_name,
          amount: el.amount,
          period: el.period,
          type: el.type,
          amount_type: 'total',
          operation: this.operation,
          expression: this.getExpression(el),
          scales: el.scales
        };
        el.id ? (this.regularPayments[i].id = el.id) : '';
      });
    }
  }

  private buildPaymentFormArray(data) {
    if (data && this.paymentsArray) {
      data.forEach((el, i) => {
        this.paymentsArray.setControl(i, this.buildPaymentsFomsControls(el));
      });
    }
  }

  private clearFormArray() {
    if (this.paymentsArray) {
      this.paymentsArray.controls = [];
      this.paymentsArray.setControl(0, this.buildPaymentsFomsControls());
    }
    this.regularPayments = [];
  }

  private initPaymentsForm() {
    this.regularPaymentsForm = this.fb.group({
      payments: this.paymentsArray
    });
  }

  private timeValueSource(obj) {
    if (!((obj.start_time || '').indexOf(':') + 1)) {
      obj.start_time = obj.start_time.slice(0, 2) + ':' + obj.start_time.slice(2);
    }
    if (!((obj.end_time || '').indexOf(':') + 1)) {
      obj.end_time = obj.end_time.slice(0, 2) + ':' + obj.end_time.slice(2);
    }
    return obj;
  }

  private buildPaymentsFomsControls(el?: RegularPayments) {
    let convertedExpression = this.expressionsService.getConvertedExpression(el ? JSON.parse(el.expression) : null);
    return this.fb.group({
      name: [el && el.name ? el.name : '', Validators.compose([Validators.required])],
      loc_name: [el && el.loc_name ? el.loc_name : ''],
      amount: [el ? el.amount : '', Validators.compose([Validators.required, Validators.min(0)])],
      type: [el && el.type ? el.type : this.paymentsType[0], Validators.required],
      amount_type: [el && el.amount_type ? el.amount_type : this.paymentsAmountType[0], Validators.required],
      days: [convertedExpression.days_of_week],
      start_time: [
        convertedExpression.start_time,
        Validators.compose([
          Validators.maxLength(5),
          Validators.required,
          Validators.pattern('^(((([0-1][0-9])|(2[0-3])):?[0-5][0-9])|(23:?00))')
        ])
      ],
      end_time: [
        convertedExpression.end_time,
        Validators.compose([
          Validators.maxLength(5),
          Validators.required,
          Validators.pattern('^(((([0-1][0-9])|(2[0-3])):?[0-5][0-9])|(23:?00))')
        ])
      ],
      period: [el && el.period ? el.period : this.paymentsPeriods[0].id, Validators.required],
      start_day: [el && convertedExpression.start_day ? convertedExpression.start_day : 0],
      end_day: [el && convertedExpression.end_day ? convertedExpression.end_day : 0],
      id: el && el.id ? el.id : null,
      scales: el && el.scales ? [...[el.scales]] : null
    });
  }

  addNewPayment() {
    this.paymentsArray.push(this.buildPaymentsFomsControls());
    this.select(this.paymentsArray.length - 1);
  }

  copyPayment(index) {
    let copy = this.paymentsArray.getRawValue()[index];
    copy.expression = this.getExpression(copy);
    copy.id == null;
    copy.name = copy.name + ' копия';
    this.paymentsArray.push(this.buildPaymentsFomsControls(copy));
  }

  onRemovePayment(index) {
    const paymentId = this.paymentsArray.getRawValue()[index].id;
    if (paymentId) {
      this.dialog
        .open(DeleteDialogComponent, {
          data: {
            message: 'PAYMENT_REMOVE_MSG'
          }
        })
        .afterClosed()
        .subscribe(res => {
          if (res) {
            this.removeRgularPaymntInit(paymentId, index);
            this.select(index - 1);
          }
        });
    } else {
      this.paymentsArray.removeAt(index);
    }
  }

  setFormValueField(event: { key: string; value: string }, name: string, index: number): void {
    (this.paymentsFormArray.controls[index] as FormGroup).controls[name].patchValue(event.key);
    this.paymentsFormArray.markAsTouched();
    this.paymentsFormArray.markAsDirty();
  }

  private removeRgularPaymntInit(id: string, index: number) {
    this.regularPaymentsService.removeRegularPayment(id).subscribe((isRemove: boolean) => {
      if (isRemove) {
        this.paymentsArray.removeAt(index);
      }
      this.tariffService.loadTariffs();
    });
  }

  select(index: number) {
    this.selectedIndex = index;
    this.selectedPayment = this.regularPayments[index];
    this.regularPaymentsService.validationRagularPaymentsState.next(this.isDirty ? this.paymentsArray.valid : true);
  }

  ngOnDestroy() {
    this.clearFormSubs.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/driver-tariffs/regular-payments/regular-payments.component.ts